/**
 * Gap-based cart cross-sell.
 * Liquid prints every accessory as a hidden .cart-upsell--card-wrap wrapping a native
 * product-card-small inside a simple scrollable .cart-upsell--inner track. This script
 * decides which wraps to show based on the remaining free-shipping gap (active currency),
 * shows up to 4 (two near the threshold + cheaper ones), and for multi-variant products
 * replaces the native quick-view with a floating pop-up below the card.
 *
 * The pop-up is appended to <cart-upsell> (NOT inside the horizontal scroller) and
 * positioned with absolute coordinates derived from the card's on-screen position, so
 * the scroller keeps a normal overflow-x:auto (scroll + clipping work) while the pop-up
 * is free to extend downward without being clipped. The pop-up follows the card when the
 * carousel scrolls.
 *
 * The track uses CSS scroll-snap (x mandatory) so a touch swipe settles one card at a
 * time. Desktop prev/next arrows advance EXACTLY one card per click; their rAF animation
 * temporarily disables snap (via .cart-upsell--no-snap) so snap doesn't fight it, with a
 * safety timeout that always re-enables snap afterwards.
 */
if (!customElements.get('cart-upsell')) {
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.rate = (window.Shopify && Shopify.currency && Shopify.currency.rate) ? parseFloat(Shopify.currency.rate) : 1;
      this.colorLabel = this.dataset.colorLabel || 'Colour';
      this.sizeLabel = this.dataset.sizeLabel || 'Size';
      this.inner = this.querySelector('.cart-upsell--inner');
      this.wraps = Array.from(this.querySelectorAll('.cart-upsell--card-wrap'));
      this.activePopup = null;
      this.activeWrap = null;
      this.snapTimer = null;
      this.bindTriggers();
      this.setupArrows();
      this.render();
    }

    async render() {
      const minimum = Math.round(parseInt(this.dataset.minimum, 10) * this.rate);
      const total = parseInt(this.dataset.cartTotal, 10);
      const gap = minimum - total;
      if (gap <= 0) { this.hideAll(); return; }
      const SMALL_GAP = Math.round(1500 * this.rate);
      const MAX_GAP = Math.round(4000 * this.rate);
      if (gap > MAX_GAP) { this.hideAll(); return; }

      let cartVariantIds = [];
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        cartVariantIds = cart.items.map(function (i) { return i.id; });
      } catch (e) {}

      const candidates = this.wraps
        .map(function (el) {
          return { el: el, id: parseInt(el.dataset.variantId, 10), localPrice: Math.round(parseInt(el.dataset.price, 10) * this.rate) };
        }.bind(this))
        .filter(function (c) { return cartVariantIds.indexOf(c.id) === -1; })
        .sort(function (a, b) { return a.localPrice - b.localPrice; }); // cheapest first

      this.wraps.forEach(function (el) { el.setAttribute('hidden', ''); });
      if (!candidates.length) { this.hideAll(); return; }

      const TARGET = 4;
      const NEAR = 2;
      const chosen = [];
      const pushUnique = function (c) { if (c && chosen.indexOf(c) === -1) chosen.push(c); };

      const covering = candidates.filter(function (c) { return c.localPrice >= gap; }); // ascending
      const nearList = covering.length
        ? covering.slice(0, NEAR)
        : candidates.slice().reverse().slice(0, NEAR); // priciest
      nearList.forEach(pushUnique);

      for (let i = 0; i < candidates.length && chosen.length < TARGET; i++) {
        pushUnique(candidates[i]);
      }

      const finalChosen = chosen.slice(0, TARGET);
      if (!finalChosen.length) { this.hideAll(); return; }

      finalChosen.sort(function (a, b) { return a.localPrice - b.localPrice; });
      finalChosen.forEach(function (c) { c.el.removeAttribute('hidden'); });
      this.removeAttribute('hidden');
      this.updateArrows();
    }

    hideAll() { this.setAttribute('hidden', ''); }

    /* ---- Desktop carousel arrows ---- */
    setupArrows() {
      if (!this.inner || this.querySelector('.cart-upsell--arrow')) return;
      const self = this;
      const mk = function (dir) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cart-upsell--arrow cart-upsell--arrow-' + dir;
        b.setAttribute('aria-label', dir === 'prev' ? 'Previous' : 'Next');
        b.innerHTML = dir === 'prev'
          ? '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        b.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          self.closePopups();
          self.scrollByCards(dir === 'prev' ? -1 : 1);
        });
        return b;
      };
      this.prevArrow = mk('prev');
      this.nextArrow = mk('next');
      this.appendChild(this.prevArrow);
      this.appendChild(this.nextArrow);
      this.inner.addEventListener('scroll', function () { self.updateArrows(); self.repositionPopup(); }, { passive: true });
      window.addEventListener('resize', function () { self.updateArrows(); self.repositionPopup(); });
    }

    // Visible (non-hidden) card wraps, left-to-right.
    visibleCards() {
      return Array.from(this.inner.querySelectorAll('.cart-upsell--card-wrap')).filter(function (el) {
        return !el.hasAttribute('hidden');
      });
    }

    // Advance exactly one card in the given direction (+1 next, -1 prev), snapping the
    // target card's left edge to the scroller's left edge.
    scrollByCards(dir) {
      const el = this.inner;
      if (!el) return;
      const cards = this.visibleCards();
      if (!cards.length) return;
      const base = el.scrollLeft;
      const tol = 4;
      const offsets = cards.map(function (c) { return c.offsetLeft; });
      let targetLeft;
      if (dir > 0) {
        const next = offsets.find(function (o) { return o > base + tol; });
        targetLeft = (next != null) ? next : (el.scrollWidth - el.clientWidth);
      } else {
        let prev = 0;
        for (let i = 0; i < offsets.length; i++) {
          if (offsets[i] < base - tol) prev = offsets[i]; else break;
        }
        targetLeft = prev;
      }
      this.animateScrollTo(targetLeft);
    }

    // Re-enable scroll snap (remove the no-snap guard), clearing any pending safety timer.
    enableSnap() {
      if (this.snapTimer) { clearTimeout(this.snapTimer); this.snapTimer = null; }
      if (this.inner) this.inner.classList.remove('cart-upsell--no-snap');
    }

    // Animated horizontal scroll to an absolute scrollLeft (rAF, fast easeOutCubic).
    // Snap is disabled during the animation so it doesn't fight the programmatic scroll,
    // and a safety timeout guarantees it is re-enabled even if the rAF loop is interrupted.
    animateScrollTo(targetLeft) {
      const el = this.inner;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const target = Math.max(0, Math.min(max, targetLeft));
      const start = el.scrollLeft;
      const dist = target - start;
      if (Math.abs(dist) < 1) { this.updateArrows(); return; }
      const duration = 200;
      const t0 = (window.performance && performance.now) ? performance.now() : Date.now();
      const self = this;
      el.classList.add('cart-upsell--no-snap');
      if (this.snapTimer) clearTimeout(this.snapTimer);
      this.snapTimer = setTimeout(function () { self.enableSnap(); }, duration + 120);
      const ease = function (t) { return 1 - Math.pow(1 - t, 3); }; // easeOutCubic - fast start
      const step = function (now) {
        const elapsed = now - t0;
        const p = Math.min(1, elapsed / duration);
        el.scrollLeft = start + dist * ease(p);
        if (p < 1) { requestAnimationFrame(step); }
        else {
          el.scrollLeft = target;
          self.enableSnap();
          self.updateArrows();
        }
      };
      requestAnimationFrame(step);
    }

    updateArrows() {
      if (!this.inner || !this.prevArrow || !this.nextArrow) return;
      const overflow = this.inner.scrollWidth - this.inner.clientWidth;
      if (overflow <= 4) {
        this.prevArrow.classList.add('is-hidden');
        this.nextArrow.classList.add('is-hidden');
        return;
      }
      const x = this.inner.scrollLeft;
      this.prevArrow.classList.toggle('is-hidden', x <= 2);
      this.nextArrow.classList.toggle('is-hidden', x >= overflow - 2);
    }

    bindTriggers() {
      const self = this;
      this.wraps.forEach(function (wrap) {
        if (wrap.dataset.multivariant !== 'true') return;
        const trigger = wrap.querySelector('.product-card-small--add-to-cart-button');
        if (!trigger) return;
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.togglePopup(wrap);
        }, true);
      });
      document.addEventListener('click', function (e) {
        if (!e.target.closest('.cart-upsell--popup') && !e.target.closest('.product-card-small--add-to-cart-button')) {
          self.closePopups();
        }
      });
    }

    closePopups() {
      if (this.activePopup) { this.activePopup.remove(); this.activePopup = null; }
      if (this.activeWrap) { this.activeWrap.classList.remove('has-popup'); this.activeWrap = null; }
    }

    // Position the active pop-up under its card, anchored to <cart-upsell> coordinates so
    // it can extend downward freely while the scroller stays clipped.
    repositionPopup() {
      if (!this.activePopup || !this.activeWrap) return;
      const hostRect = this.getBoundingClientRect();
      const cardRect = this.activeWrap.getBoundingClientRect();
      const innerRect = this.inner.getBoundingClientRect();
      // Hide the pop-up if its card has been scrolled (mostly) out of the viewport.
      const cardCenter = cardRect.left + cardRect.width / 2;
      if (cardCenter < innerRect.left - 2 || cardCenter > innerRect.right + 2) {
        this.activePopup.style.visibility = 'hidden';
        return;
      }
      this.activePopup.style.visibility = '';
      let left = cardRect.left - hostRect.left;
      // Keep the pop-up within the host horizontally.
      const popW = this.activePopup.offsetWidth || 320;
      const maxLeft = this.offsetWidth - popW;
      if (left > maxLeft) left = Math.max(0, maxLeft);
      if (left < 0) left = 0;
      const top = cardRect.bottom - hostRect.top + 9;
      this.activePopup.style.left = left + 'px';
      this.activePopup.style.top = top + 'px';
      // Arrow points to the card; align it to the card's left area.
      const arrowLeft = (cardRect.left - hostRect.left) - left + 28;
      this.activePopup.style.setProperty('--cu-arrow-left', arrowLeft + 'px');
    }

    togglePopup(wrap) {
      const wasOpen = (this.activeWrap === wrap);
      this.closePopups();
      if (wasOpen) return;

      let data;
      try { data = JSON.parse(wrap.querySelector('.cart-upsell--data').textContent.trim()); } catch (e) { return; }
      if (!data || !data.variants || !data.variants.length) return;

      // Snap the card to the start so it (and its pop-up) are fully visible.
      this.animateScrollTo(wrap.offsetLeft);

      const self = this;
      const optionNames = data.optionNames || [];
      const optionCount = optionNames.length;
      const colorIndex = data.colorIndex || 0; // 1-based, 0 = no colour option
      const hasColor = colorIndex > 0;
      const colorKey = hasColor ? 'option' + colorIndex : null;

      let sizeOptPos = 0; // 1-based
      for (let i = 1; i <= optionCount; i++) {
        if (i !== colorIndex) { sizeOptPos = i; break; }
      }
      const sizeKey = sizeOptPos > 0 ? 'option' + sizeOptPos : null;
      const sizeName = sizeOptPos > 0 ? (optionNames[sizeOptPos - 1] || self.sizeLabel) : self.sizeLabel;
      const colorName = hasColor ? (optionNames[colorIndex - 1] || self.colorLabel) : self.colorLabel;

      const colors = [];
      const colorSeen = {};
      const sizes = [];
      const sizeSeen = {};
      data.variants.forEach(function (v) {
        if (hasColor) {
          const cval = v[colorKey];
          if (cval != null && cval !== '' && !colorSeen[cval]) {
            colorSeen[cval] = true;
            colors.push({ value: cval, name: (data.colorNames && data.colorNames[cval]) || cval, image: v.image });
          }
        }
        if (sizeKey) {
          const sval = v[sizeKey];
          if (sval != null && sval !== '' && !sizeSeen[sval]) { sizeSeen[sval] = true; sizes.push(sval); }
        }
      });

      let showSizes = sizeKey && sizes.length > 0;
      if (showSizes && hasColor && sizes.length === 1) {
        const only = (sizes[0] || '').toLowerCase();
        if (only === 'one size' || only === 'default title' || only === 'taglia unica' || only === 'os' || only === 'einheitsgröße' || only === 'unica') {
          showSizes = false;
        }
      }

      const state = { color: null, size: null };

      const popup = document.createElement('div');
      popup.className = 'cart-upsell--popup';
      const colCount = (hasColor ? 1 : 0) + (showSizes ? 1 : 0);
      popup.classList.add(colCount === 2 ? 'cart-upsell--popup--two' : 'cart-upsell--popup--one');

      let leftHTML = '';
      if (hasColor) {
        leftHTML = '<div class="cart-upsell--popup-col cart-upsell--popup-colors">' +
          '<div class="cart-upsell--popup-label">' + colorName + '</div>' +
          '<div class="cart-upsell--color-list">' +
          colors.map(function (c) {
            const img = c.image ? '<img src="' + c.image + '" alt="" loading="lazy">' : '';
            return '<button type="button" class="cart-upsell--color-row" data-color="' + encodeURIComponent(c.value) + '">' +
              '<span class="cart-upsell--color-thumb">' + img + '</span>' +
              '<span class="cart-upsell--color-name">' + c.name + '</span>' +
            '</button>';
          }).join('') +
          '</div>' +
        '</div>';
      }

      let rightHTML = '';
      if (showSizes) {
        rightHTML = '<div class="cart-upsell--popup-col cart-upsell--popup-sizes">' +
          '<div class="cart-upsell--popup-label">' + sizeName + '</div>' +
          '<div class="cart-upsell--size-set">' +
          sizes.map(function (s) {
            return '<button type="button" class="cart-upsell--size-btn" data-size="' + encodeURIComponent(s) + '"><span>' + s + '</span></button>';
          }).join('') +
          '</div>' +
        '</div>';
      }

      popup.innerHTML = leftHTML + rightHTML;

      function findVariant() {
        return data.variants.find(function (v) {
          let ok = true;
          if (hasColor) ok = ok && (v[colorKey] === state.color);
          if (showSizes) ok = ok && (v[sizeKey] === state.size);
          return ok;
        });
      }

      function refreshSizeAvailability() {
        if (!showSizes) return;
        popup.querySelectorAll('.cart-upsell--size-btn').forEach(function (btn) {
          const sval = decodeURIComponent(btn.dataset.size);
          let avail;
          if (hasColor && state.color != null) {
            const match = data.variants.find(function (v) { return v[colorKey] === state.color && v[sizeKey] === sval; });
            avail = !!(match && match.available);
          } else {
            const match = data.variants.find(function (v) { return v[sizeKey] === sval; });
            avail = !!(match && match.available);
          }
          btn.classList.toggle('is-disabled', !avail);
        });
      }

      function tryAdd() {
        const variant = findVariant();
        if (!variant || !variant.available) return;
        self.addVariant(variant.id, popup);
      }

      popup.querySelectorAll('.cart-upsell--color-row').forEach(function (row) {
        row.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          state.color = decodeURIComponent(row.dataset.color);
          popup.querySelectorAll('.cart-upsell--color-row').forEach(function (r) { r.classList.remove('is-selected'); });
          row.classList.add('is-selected');
          refreshSizeAvailability();
          if (!showSizes) { tryAdd(); }
          else if (state.size != null) { tryAdd(); }
        });
      });

      popup.querySelectorAll('.cart-upsell--size-btn').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          if (btn.classList.contains('is-disabled')) return;
          state.size = decodeURIComponent(btn.dataset.size);
          popup.querySelectorAll('.cart-upsell--size-btn').forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          if (!hasColor || state.color != null) tryAdd();
        });
      });

      // Append to host (outside the scroller) and position under the card.
      wrap.classList.add('has-popup');
      this.appendChild(popup);
      this.activePopup = popup;
      this.activeWrap = wrap;
      // Position now and again after the snap animation settles.
      this.repositionPopup();
      const self2 = this;
      requestAnimationFrame(function () { self2.repositionPopup(); });
      setTimeout(function () { self2.repositionPopup(); }, 230);
    }

    addVariant(variantId, popup) {
      const self = this;
      if (popup) popup.classList.add('loading');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], attributes: { _cart_upsell: 'free-shipping-bar' } })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          self.closePopups();
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        })
        .catch(function () { if (popup) popup.classList.remove('loading'); });
    }
  }
  customElements.define('cart-upsell', CartUpsell);
}
